/**
 * equipmentVisuals.ts — Pure shared equipment-visual resolver.
 *
 * BIG-EQUIP-VIS-01 S1 + S2 correction. The single item-ID → visual-descriptor
 * projection used by both local and remote player rendering. Pure: no
 * Colyseus, React, Pixi, renderer, character-class, inventory, or
 * cosmetic-appearance dependencies.
 *
 * Authority flows one way:
 *   Player.equipment (eight item IDs)
 *     → ITEM_DATABASE[id].visual  (generated descriptor, authored in TOML)
 *       → resolveItemEquipmentVisual()  (this module)
 *         → ResolvedPlayerEquipmentVisuals  (one optional descriptor per slot)
 *
 * Fallback rules (blueprint §Fallback rules):
 *  1. empty / null / undefined  → `empty` (no layer).
 *  2. unknown item ID           → `unknown_item` (ID preserved, no layer).
 *  3. known item, no descriptor  → `missing_descriptor` (no layer).
 *  4. descriptor invariant violated → `slot_mismatch` (no layer, fail closed).
 *  5. No name parsing, no default sword/armor, no class fallback.
 *
 * A descriptor is `resolved` only when ALL invariants agree:
 *   descriptor.itemId === requested itemId
 *   descriptor.itemSlot === item.equipmentSlot
 *   descriptor.itemSlot maps to expectedSlot via the canonical slot map
 *   descriptor.schemaSlot === expectedSlot
 *   descriptor.layer === expectedSlot
 *   authored family is in the canonical family registry for this slot
 *   authored family/variant/color fields remain structurally valid
 * Otherwise the resolution fails closed as `slot_mismatch`.
 *
 * The canonical family registry (EQUIPMENT_VISUAL_FAMILIES) is the single
 * shared authority for which family strings the renderer can actually render.
 * Renderer registries consume or are exhaustively checked against it. This
 * keeps the future zero-coverage gate truthful: "resolved" must mean the
 * renderer can actually render the family.
 *
 * Waking coverage is a deterministic fixture only. Global runtime cutover is
 * blocked until `computeEquipmentVisualCoverage()` reports zero unresolved
 * slot-mapped items.
 */

import {
  EQUIPMENT_SCHEMA_SLOTS,
  EquipmentSchemaSlot,
  ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT,
  ItemEquipmentSlot,
} from './equipmentSlots';
import { ITEM_DATABASE } from './content/generated/items.gen';

// ─── Types ─────────────────────────────────────────────────────

/**
 * The render layer a descriptor drives. Identical in vocabulary to
 * `EquipmentSchemaSlot` — each schema slot owns exactly one visual layer.
 */
export type EquipmentVisualLayer = EquipmentSchemaSlot;

/**
 * Explicit, sufficient-for-rendering visual descriptor for one equipped item.
 * Authored in TOML (`[<item>.visual]`) and generated into `items.gen.ts`.
 *
 * `itemId`, `itemSlot`, `schemaSlot`, and `layer` are derived at code-gen time
 * from the item's `equipment_slot` so the descriptor and the item's own slot
 * cannot drift. `family`, `variant`, `primaryColor`, and optional `accentColor`
 * are the authored palette/shape inputs the renderer consumes.
 */
export interface ItemEquipmentVisualDescriptor {
  itemId: string;
  itemSlot: ItemEquipmentSlot;
  schemaSlot: EquipmentSchemaSlot;
  layer: EquipmentVisualLayer;
  family: string;
  variant: number;
  primaryColor: string;
  accentColor?: string;
}

export type EquipmentVisualResolution =
  | { kind: 'empty'; schemaSlot: EquipmentSchemaSlot }
  | { kind: 'resolved'; descriptor: ItemEquipmentVisualDescriptor }
  | { kind: 'unknown_item'; itemId: string; schemaSlot: EquipmentSchemaSlot }
  | { kind: 'missing_descriptor'; itemId: string; schemaSlot: EquipmentSchemaSlot }
  | { kind: 'slot_mismatch'; itemId: string; schemaSlot: EquipmentSchemaSlot };

/**
 * Full eight-slot resolved loadout. Every schema slot is present exactly once;
 * unresolved slots carry their failure kind so the renderer can omit that
 * layer without guessing.
 */
export type ResolvedPlayerEquipmentVisuals = {
  [slot in EquipmentSchemaSlot]: EquipmentVisualResolution;
};

// ─── Canonical family taxonomy ─────────────────────────────────
// The single shared authority for which family strings the renderer can
// actually render per slot. Renderer registries must consume or be
// exhaustively checked against this registry. S3 content authoring may
// only use families listed here; synonyms are explicitly assigned during
// authoring, never parsed from item names at runtime.
//
// Adding a family requires:
//   1. Adding it to EQUIPMENT_VISUAL_FAMILIES below
//   2. Adding a renderer path in the renderer registries
//   3. Adding a pose mapping in WEAPON_FAMILY_TO_POSE_TYPE (mainHand only)
//   4. Adding an exhaustive test entry

export const EQUIPMENT_VISUAL_FAMILIES: Readonly<Record<EquipmentSchemaSlot, readonly string[]>> = {
  mainHand: [
    'sword', 'wand', 'bow', 'crossbow', 'staff', 'dagger', 'axe', 'mace',
    'spear', 'shield', 'tome', 'glaive', 'fishing_pole', 'totem',
  ],
  head: ['hood', 'helm', 'hat', 'crown', 'headband', 'mask', 'antlers', 'candle'],
  chest: ['plate', 'mail', 'robe', 'tunic', 'vest', 'shirt'],
  legs: ['plate', 'mail', 'leggings', 'pants', 'skirt'],
  feet: ['boots', 'sabatons', 'slippers', 'moccasins'],
  hands: ['gloves', 'gauntlets'],
  back: ['cloak'],
  neck: ['pendant', 'amulet', 'necklace', 'ring', 'trinket'],
  // Lane BK (B26): the Lantern slot draws no gear layer on the ghost SVG — its
  // light is a Pixi glow (GhostLanternFx, src/shared/lanternStyle.ts). No
  // families, so lantern items carry no [visual] and sit outside coverage.
  charm: [],
  // The Flame slot: also a Pixi light (flameStyleForEquipment, lanternStyle.ts), no SVG layer.
  halo: [],
};

/**
 * Check whether a family is in the canonical registry for a given slot.
 * Pure, deterministic, no I/O.
 */
export function isSupportedEquipmentVisualFamily(
  slot: EquipmentSchemaSlot,
  family: string,
): boolean {
  const families = EQUIPMENT_VISUAL_FAMILIES[slot];
  return families.includes(family);
}

/**
 * Pose category for weapon-family anchor derivation.
 * Maps each mainHand family to a WeaponType-like pose category that
 * AttachmentGeometry understands. Non-mainHand families are not listed.
 * Unknown families must never fall back to sword — callers check
 * isSupportedEquipmentVisualFamily first.
 *
 * The pose category determines hand/foot anchor positions during the
 * attack pose. For non-attack poses, the category has no effect.
 */
export const WEAPON_FAMILY_TO_POSE_TYPE: Readonly<Record<string, 'sword' | 'staff' | 'bow' | 'axe' | 'dagger' | 'mace' | 'wand'>> = {
  sword: 'sword',
  wand: 'wand',
  bow: 'bow',
  crossbow: 'bow',
  staff: 'staff',
  dagger: 'dagger',
  axe: 'axe',
  mace: 'mace',
  spear: 'sword',
  shield: 'sword',
  tome: 'sword',
  glaive: 'sword',
  fishing_pole: 'sword',
  totem: 'staff',
};

/**
 * Derive the pose category from a resolved mainHand descriptor's family.
 * Returns undefined for unsupported families or non-mainHand descriptors.
 * This is the single authority used by both GhostRenderer (for pose/anchor
 * computation) and WeaponParts (for hand-anchor derivation).
 */
export function poseTypeForFamily(family: string): 'sword' | 'staff' | 'bow' | 'axe' | 'dagger' | 'mace' | 'wand' | undefined {
  return WEAPON_FAMILY_TO_POSE_TYPE[family];
}

// ─── Authored-field structural validation ──────────────────────
// Mirrors the Zod contracts in scripts/content-schemas.ts so the resolver can
// fail closed if a generated descriptor's authored fields are corrupt (e.g.
// a hand-edit or generation bug). These are intentionally pure regex/range
// checks — no color parsing, no family registry lookup.

const HEX_COLOR_RE = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const FAMILY_ID_RE = /^[a-z][a-z0-9_-]*$/;

function isAuthoredFieldValid(d: ItemEquipmentVisualDescriptor): boolean {
  if (typeof d.family !== 'string' || !FAMILY_ID_RE.test(d.family)) return false;
  if (!Number.isInteger(d.variant) || d.variant < 0 || d.variant > 99) return false;
  if (typeof d.primaryColor !== 'string' || !HEX_COLOR_RE.test(d.primaryColor)) return false;
  if (d.accentColor !== undefined) {
    if (typeof d.accentColor !== 'string' || !HEX_COLOR_RE.test(d.accentColor)) return false;
  }
  return true;
}

// ─── Resolver ──────────────────────────────────────────────────

/**
 * Resolve a single equipment item ID against an expected schema slot.
 *
 * Pure and deterministic: given the same `ITEM_DATABASE` and inputs, the
 * output is identical. Never substitutes, deletes, or infers.
 *
 * A descriptor resolves only when every invariant agrees (itemId, itemSlot,
 * schemaSlot, layer, slot mapping, and authored-field structural validity).
 * Any invariant violation fails closed as `slot_mismatch`.
 */
export function resolveItemEquipmentVisual(
  itemId: string | null | undefined,
  expectedSlot: EquipmentSchemaSlot,
): EquipmentVisualResolution {
  // Rule 1: empty / null / undefined → empty, no layer.
  if (itemId === null || itemId === undefined || itemId === '') {
    return { kind: 'empty', schemaSlot: expectedSlot };
  }

  // Rule 2: unknown item ID → unknown_item, ID preserved.
  const item = ITEM_DATABASE[itemId];
  if (!item) {
    return { kind: 'unknown_item', itemId, schemaSlot: expectedSlot };
  }

  // Rule 3: known item without a descriptor → missing_descriptor.
  const descriptor = item.visual;
  if (!descriptor) {
    return { kind: 'missing_descriptor', itemId, schemaSlot: expectedSlot };
  }

  // Rule 4: descriptor invariant violated → slot_mismatch (fail closed).
  // All applicable invariants must agree before a descriptor is trusted.
  if (!isDescriptorValidForSlot(descriptor, itemId, expectedSlot, item.equipmentSlot)) {
    return { kind: 'slot_mismatch', itemId, schemaSlot: expectedSlot };
  }

  // Rule 5: resolved — return the generated descriptor verbatim.
  return { kind: 'resolved', descriptor };
}

/**
 * Check every descriptor invariant. Returns true only when all agree.
 * Exported for adversarial test access.
 *
 * Invariants checked:
 *   descriptor.itemId === expectedItemId
 *   descriptor.itemSlot === expectedItemSlot
 *   descriptor.schemaSlot === expectedSlot
 *   descriptor.layer === expectedSlot
 *   descriptor.itemSlot maps to expectedSlot via the canonical slot map
 *   descriptor.family is in the canonical family registry for expectedSlot
 *   authored family/variant/color fields remain structurally valid
 */
export function isDescriptorValidForSlot(
  descriptor: ItemEquipmentVisualDescriptor,
  expectedItemId: string,
  expectedSlot: EquipmentSchemaSlot,
  expectedItemSlot: string | undefined,
): boolean {
  // Derived-field invariants.
  if (descriptor.itemId !== expectedItemId) return false;
  if (descriptor.itemSlot !== expectedItemSlot) return false;
  if (descriptor.schemaSlot !== expectedSlot) return false;
  if (descriptor.layer !== expectedSlot) return false;
  // itemSlot must map to expectedSlot via the canonical slot map.
  if (ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT[descriptor.itemSlot] !== expectedSlot) return false;
  // Family must be in the canonical registry for this slot.
  if (!isSupportedEquipmentVisualFamily(expectedSlot, descriptor.family)) return false;
  // Authored fields must remain structurally valid.
  if (!isAuthoredFieldValid(descriptor)) return false;
  return true;
}

// ─── Full-loadout composition ──────────────────────────────────

/**
 * Resolve all eight authoritative schema slots from a read-only item-ID record.
 *
 * Missing keys are treated as empty for that slot. The record is read once;
 * this function performs no mutation and no I/O. Deterministic for a given
 * `ITEM_DATABASE` + input record.
 */
export function resolvePlayerEquipmentVisuals(
  equippedItemIds: Readonly<Partial<Record<EquipmentSchemaSlot, string>>>,
): ResolvedPlayerEquipmentVisuals {
  const result = {} as ResolvedPlayerEquipmentVisuals;
  for (const slot of EQUIPMENT_SCHEMA_SLOTS) {
    result[slot] = resolveItemEquipmentVisual(equippedItemIds[slot], slot);
  }
  return result;
}

// ─── Unresolved-coverage report ────────────────────────────────

/**
 * Deterministic coverage report over the current generated item database.
 *
 * Classifies every item in `ITEM_DATABASE`:
 *  - `totalSlotMappedItems`: items whose `equipmentSlot` is one of the eight
 *    H2000 slots. This is the universe that must reach zero unresolved before
 *    runtime cutover (blueprint D8).
 *  - `resolved`: slot-mapped items with a valid visual descriptor where every
 *    invariant agrees (itemId, itemSlot, schemaSlot, layer, slot mapping,
 *    authored-field validity).
 *  - `missingDescriptor`: slot-mapped items with no visual descriptor.
 *  - `invalidOrMismatched`: slot-mapped items whose descriptor fails any
 *    invariant (should be impossible for generated data, but reported and
 *    counted defensively).
 *  - `unknownUnclassified`: items with no equipmentSlot or a non-mapped slot
 *    (consumables, materials, slots with no equipment-visual mapping). Reported for completeness;
 *    not part of the cutover gate.
 *
 * ID lists are sorted alphabetically so the report is byte-stable across runs
 * regardless of object insertion order.
 */
export interface EquipmentVisualCoverageReport {
  totalItems: number;
  totalSlotMappedItems: number;
  resolved: number;
  missingDescriptor: number;
  invalidOrMismatched: number;
  unknownUnclassified: number;
  resolvedIds: string[];
  missingDescriptorIds: string[];
  invalidOrMismatchedIds: string[];
  unknownUnclassifiedIds: string[];
}

/** Own-property membership check (avoids prototype-chain `in` gotchas). */
function hasOwn(obj: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function computeEquipmentVisualCoverage(): EquipmentVisualCoverageReport {
  const resolvedIds: string[] = [];
  const missingDescriptorIds: string[] = [];
  const invalidOrMismatchedIds: string[] = [];
  const unknownUnclassifiedIds: string[] = [];

  const sortedIds = Object.keys(ITEM_DATABASE).sort();
  const slotMap = ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT as Record<string, EquipmentSchemaSlot>;

  for (const id of sortedIds) {
    const item = ITEM_DATABASE[id];
    const itemSlot = item.equipmentSlot;
    // A slot with no gear families (the Lantern) has no visual layer to cover.
    if (!itemSlot || !hasOwn(slotMap, itemSlot) || EQUIPMENT_VISUAL_FAMILIES[slotMap[itemSlot]].length === 0) {
      unknownUnclassifiedIds.push(id);
      continue;
    }

    const expectedSchemaSlot = slotMap[itemSlot];
    const descriptor = item.visual;
    if (!descriptor) {
      missingDescriptorIds.push(id);
      continue;
    }

    // Fail closed: every invariant must agree, including layer and authored
    // field validity. Generated descriptors derive all fields from
    // equipmentSlot, so a failure indicates generation corruption.
    if (!isDescriptorValidForSlot(descriptor, id, expectedSchemaSlot, itemSlot)) {
      invalidOrMismatchedIds.push(id);
      continue;
    }

    resolvedIds.push(id);
  }

  return {
    totalItems: sortedIds.length,
    totalSlotMappedItems: resolvedIds.length + missingDescriptorIds.length + invalidOrMismatchedIds.length,
    resolved: resolvedIds.length,
    missingDescriptor: missingDescriptorIds.length,
    invalidOrMismatched: invalidOrMismatchedIds.length,
    unknownUnclassified: unknownUnclassifiedIds.length,
    resolvedIds,
    missingDescriptorIds,
    invalidOrMismatchedIds,
    unknownUnclassifiedIds,
  };
}

/** Convenience re-export of the schema-slot set for consumers. */
export { EQUIPMENT_SCHEMA_SLOTS };
